import { FormikProvider, useFormik } from 'formik';

import BackButton from '@perpay-web/components/base/BackButton/BackButton';
import CustomButton from '@perpay-web/components/base/CustomButton/CustomButton';
import FormikTextInput from '@perpay-web/components/base/TextInput/FormikTextInput';
import FocusContent from '@perpay-web/components/composite/FocusLayout/FocusContent';
import FocusFooter from '@perpay-web/components/composite/FocusLayout/FocusFooter';
import FocusHeader from '@perpay-web/components/composite/FocusLayout/FocusHeader';
import FocusLayout from '@perpay-web/components/composite/FocusLayout/FocusLayout';
import Stack from '@perpay-web/components/design-system/Stack/Stack';
import CardCta from '@perpay-web/fintech/components/base/CardCta/CardCta';
import { confirmField } from '@perpay-web/fintech/validators/formBased';
import { required } from '@perpay-web/validators/fieldBased';
import { validateForm } from '@perpay-web/validators/formBased';

import styles from './ConfirmBankAccountForm.scss';

const initialValues = {
    confirmAccountNumber: '',
    confirmRoutingNumber: '',
};

const validationSchema = {
    confirmAccountNumber: [required],
    confirmRoutingNumber: [required],
};

/**
 * @param {Object} props
 * @param {Object} props.validatedData
 * @param {string} props.validatedData.accountNumber
 * @param {string} props.validatedData.routingNumber
 * @param {Function} props.onConfirmationSuccess
 * @param {Function} props.onBack
 * @param {('card')} [props.buttonTheme] - The theme of the button to use for the confirm bank account button.
 * @param {boolean} [props.loading] - Whether to show a loading state for the confirm bank account button.
 * @param {number} [props.totalSteps] - The total number of steps in the flow.
 */
export const ConfirmBankAccountForm = ({
    validatedData,
    onConfirmationSuccess,
    onBack,
    buttonTheme,
    loading,
    totalSteps,
}) => {
    const formik = useFormik({
        initialValues,
        validateOnMount: true,
        validate: (values) => {
            const fieldErrors = validateForm(values, validationSchema);

            const crossFieldErrors = {
                ...confirmField(
                    'validatedAccountNumber',
                    'confirmAccountNumber',
                    'The account numbers do not match',
                )({
                    validatedAccountNumber: validatedData.accountNumber,
                    confirmAccountNumber: values.confirmAccountNumber,
                }),
                ...confirmField(
                    'validatedRoutingNumber',
                    'confirmRoutingNumber',
                    'The routing numbers do not match',
                )({
                    validatedRoutingNumber: validatedData.routingNumber,
                    confirmRoutingNumber: values.confirmRoutingNumber,
                }),
            };

            return { ...fieldErrors, ...crossFieldErrors };
        },
        onSubmit: (values) => {
            // Combine validated data with confirmation
            const finalData = {
                ...validatedData,
                confirmAccountNumber: values.confirmAccountNumber,
                confirmRoutingNumber: values.confirmRoutingNumber,
            };
            onConfirmationSuccess(finalData);
        },
    });

    const hasErrors = Object.keys(formik.errors).length > 0;

    return (
        <FormikProvider value={formik}>
            <FocusLayout className={styles['focus-layout']}>
                <FocusHeader
                    steps={totalSteps}
                    currentStep={2}
                    leftButtonElement={<BackButton onClick={onBack} />}
                />
                <FocusContent>
                    <Stack className={styles['outer-stack']}>
                        <Stack className={styles['inner-stack-tight']}>
                            <p className={styles.title}>Confirm Bank</p>
                            <p className={styles.subtitle}>
                                Re-enter your bank account details to confirm
                                your bank
                            </p>
                        </Stack>
                        <Stack className={styles['inner-stack']}>
                            <FormikTextInput
                                name='confirmAccountNumber'
                                label='Account Number'
                            />
                            <FormikTextInput
                                name='confirmRoutingNumber'
                                label='Routing Number'
                            />
                        </Stack>
                    </Stack>
                </FocusContent>
                <FocusFooter>
                    {buttonTheme === 'card' ? (
                        <CardCta
                            Component={CustomButton}
                            mode='dark'
                            type='submit'
                            onClick={formik.handleSubmit}
                            loading={loading}
                            disabled={Boolean(formik.touched) && hasErrors}
                        >
                            Confirm Bank Details
                        </CardCta>
                    ) : (
                        <CustomButton
                            action='primary'
                            onClick={formik.handleSubmit}
                            className={styles['confirm-bank-button']}
                            disabled={Boolean(formik.touched) && hasErrors}
                            loading={loading}
                        >
                            Confirm Bank Details
                        </CustomButton>
                    )}
                </FocusFooter>
            </FocusLayout>
        </FormikProvider>
    );
};
